import { PastCourses } from "../components/PastCourses";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError, mediaUrl } from "../lib/api";
import { toast } from "sonner";
import { GraduationCap, BookOpen, Clock, Check, ChevronRight, Lock, Info, Target, FileText, Eye, MessageSquare } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { DocViewer } from "../components/DocViewer";
import { MarkdownLite, courseFiles } from "../components/RichText";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";
const hasInfo = (c) => !!(c.information?.trim() || c.learning_goals?.trim() || courseFiles(c).length);

export default function PortalClasses() {
  const navigate = useNavigate();
  const [enrolled, setEnrolled] = useState([]);
  const [available, setAvailable] = useState([]);
  const [registering, setRegistering] = useState(null);
  const [cohortFor, setCohortFor] = useState(null);
  const [chosenCohort, setChosenCohort] = useState("");
  const [detailFor, setDetailFor] = useState(null);
  const [viewer, setViewer] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/portal/courses"); setEnrolled(data.enrolled || []); setAvailable(data.available || []); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const doRegister = async (c, cohortId) => {
    setRegistering(c.id);
    try {
      await api.post(`/portal/courses/${c.id}/register`, { cohort_id: cohortId || "" });
      toast.success("Registration sent! A leader will review and approve it.");
      setCohortFor(null); setChosenCohort(""); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setRegistering(null); }
  };
  const register = async (c) => {
    if ((c.cohorts || []).length > 0) { setCohortFor(c); setChosenCohort(c.cohorts[0].id); return; }
    doRegister(c, "");
  };

  const current = enrolled.filter((c) => !c.finished);
  const past = enrolled.filter((c) => c.finished);
  const enrolledCard = (c) => (
              <button key={c.id} data-testid={`enrolled-course-${c.id}`} onClick={() => navigate(`/my-classes/${c.id}`)} className={`${CARD} p-5 text-left hover:shadow-md transition-all`}>
                {c.cover_image ? (
                  <img src={mediaUrl(c.cover_image.file_id)} alt="" className="-mx-5 -mt-5 mb-3 h-28 w-[calc(100%+2.5rem)] object-cover rounded-t-2xl" />
                ) : null}
                <div className="flex items-start justify-between">
                  <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ background: "#17A88A1A" }}><BookOpen className="h-6 w-6" style={{ color: "#17A88A" }} /></div>
                  {c.new_replies > 0 ? (
                    <span data-testid={`course-new-replies-${c.id}`} className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold text-white" style={{ background: "var(--sj-primary)" }}>
                      <MessageSquare className="h-3.5 w-3.5" /> {c.new_replies} new {c.new_replies === 1 ? "reply" : "replies"}
                    </span>
                  ) : (
                    <ChevronRight className="h-5 w-5" style={{ color: "var(--sj-text-3)" }} />
                  )}
                </div>
                <h3 className="mt-3 font-head text-lg font-semibold leading-tight">{c.title}</h3>
                {c.finished && <span data-testid={`course-finished-tag-${c.id}`} className="inline-block mt-1 text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#EDF0FF", color: "#3B5BDB" }}>Finished</span>}
                {c.description && <p className="text-sm mt-1 line-clamp-2" style={{ color: "var(--sj-text-2)" }}>{c.description}</p>}
                <div className="mt-4">
                  <div className="flex items-center justify-between text-xs mb-1" style={{ color: "var(--sj-text-3)" }}>
                    <span>{c.progress?.completed || 0}/{c.progress?.total || 0} lessons</span>
                    <span>{c.progress?.percent || 0}%</span>
                  </div>
                  <div className="h-2 rounded-full overflow-hidden" style={{ background: "var(--sj-border)" }}>
                    <div className="h-full rounded-full" style={{ width: `${c.progress?.percent || 0}%`, background: "var(--sj-secondary)" }} />
                  </div>
                </div>
              </button>
  );
  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="portal-classes-page">
      <div className="sj-page-hero">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em]" style={{ color: "var(--sj-text-3)" }}>
          <GraduationCap className="h-4 w-4" /> Discipleship
        </div>
        <h1 className="mt-2 font-head text-3xl font-extrabold tracking-tight">My Courses</h1>
        <p style={{ color: "var(--sj-text-2)" }}>Grow in your walk — register for a course and track your progress.</p>
      </div>

      <section>
        <h2 className="font-head text-xl font-semibold mb-3">Enrolled</h2>
        {current.length === 0 ? (
          <div className={`${CARD} p-8 text-center text-sm`} style={{ color: "var(--sj-text-3)" }}>{past.length ? "No current courses. Your finished courses are under Past courses below." : "You're not enrolled in any course yet. Register from the list below."}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="enrolled-courses">
            {current.map(enrolledCard)}
          </div>
        )}
        <PastCourses count={past.length} testid="portal-past-courses">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{past.map(enrolledCard)}</div>
        </PastCourses>
      </section>

      <section>
        <h2 className="font-head text-xl font-semibold mb-3">Available Courses</h2>
        {available.length === 0 ? (
          <div className={`${CARD} p-8 text-center text-sm`} style={{ color: "var(--sj-text-3)" }}>No other courses available right now.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="available-courses">
            {available.map((c) => (
              <div key={c.id} data-testid={`available-course-${c.id}`} className={`${CARD} p-5 flex flex-col`}>
                {c.cover_image ? (
                  <img src={mediaUrl(c.cover_image.file_id)} alt="" className="-mx-5 -mt-5 mb-3 h-32 w-[calc(100%+2.5rem)] object-cover rounded-t-2xl" />
                ) : (
                  <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ background: "#0B5E4E1A" }}><GraduationCap className="h-6 w-6" style={{ color: "#0B5E4E" }} /></div>
                )}
                <h3 className="mt-3 font-head text-lg font-semibold leading-tight">{c.title}</h3>
                {c.description && <p className="text-sm mt-1 line-clamp-2 flex-1" style={{ color: "var(--sj-text-2)" }}>{c.description}</p>}
                <div className="mt-4 flex items-center gap-2 flex-wrap">
                  {hasInfo(c) && (
                    <button data-testid={`course-details-${c.id}`} onClick={() => setDetailFor(c)} className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold border hover:bg-[#E3F1EC]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}><Info className="h-4 w-4" /> Course details</button>
                  )}
                  {c.registration_status === "pending" ? (
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: "#084438" }}><Clock className="h-4 w-4" /> Awaiting approval</span>
                  ) : c.registration_status === "closed" ? (
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: "var(--sj-text-3)" }}><Lock className="h-4 w-4" /> Registration closed</span>
                  ) : c.registration_status === "rejected" ? (
                    <button data-testid={`register-${c.id}`} disabled={registering === c.id} onClick={() => register(c)} className="rounded-full px-4 py-2 text-sm font-semibold border hover:bg-[#E3F1EC] disabled:opacity-60" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>Register again</button>
                  ) : (
                    <button data-testid={`register-${c.id}`} disabled={registering === c.id} onClick={() => register(c)} className="rounded-full px-5 py-2 text-sm font-semibold text-white hover:brightness-105 disabled:opacity-60" style={{ background: "var(--sj-primary)" }}>{registering === c.id ? "Sending..." : "Register"}</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <Dialog open={!!cohortFor} onOpenChange={(v) => !v && setCohortFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-head text-2xl">Choose your cohort</DialogTitle><DialogDescription>Pick which intake of this course you'd like to join.</DialogDescription></DialogHeader>
          <div className="space-y-2 mt-1">
            <p className="text-sm" style={{ color: "var(--sj-text-3)" }}>Which intake of <b>{cohortFor?.title}</b> would you like to join?</p>
            {(cohortFor?.cohorts || []).map((ch) => (
              <label key={ch.id} data-testid={`pick-cohort-${ch.id}`} className="flex items-center gap-2.5 rounded-xl border px-3 py-2.5 cursor-pointer" style={{ borderColor: chosenCohort === ch.id ? "var(--sj-primary)" : "var(--sj-border)" }}>
                <input type="radio" name="cohort" checked={chosenCohort === ch.id} onChange={() => setChosenCohort(ch.id)} className="h-4 w-4 accent-[#0B5E4E]" />
                <span className="text-sm font-medium">{ch.name}</span>
              </label>
            ))}
            <DialogFooter>
              <button onClick={() => setCohortFor(null)} className="rounded-full px-5 py-2.5 border font-medium" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>Cancel</button>
              <button data-testid="confirm-cohort-register-btn" disabled={registering === cohortFor?.id} onClick={() => doRegister(cohortFor, chosenCohort)} className="rounded-full px-6 py-2.5 text-white font-semibold disabled:opacity-60" style={{ background: "var(--sj-primary)" }}>{registering === cohortFor?.id ? "Sending…" : "Register"}</button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!detailFor} onOpenChange={(v) => !v && setDetailFor(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-head text-2xl">{detailFor?.title}</DialogTitle>
            <DialogDescription>Course information and learning goals.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-1 max-h-[60vh] overflow-auto pr-1" data-testid="course-detail-body">
            {detailFor?.information?.trim() && (
              <div>
                <div className="flex items-center gap-2 text-sm font-semibold mb-1" style={{ color: "var(--sj-primary)" }}><Info className="h-4 w-4" /> About this course</div>
                <MarkdownLite text={detailFor.information} className="text-sm" style={{ color: "var(--sj-text-2)" }} />
              </div>
            )}
            {detailFor?.learning_goals?.trim() && (
              <div>
                <div className="flex items-center gap-2 text-sm font-semibold mb-1" style={{ color: "var(--sj-primary)" }}><Target className="h-4 w-4" /> Learning goals</div>
                <ul className="text-sm space-y-1" style={{ color: "var(--sj-text-2)" }}>
                  {detailFor.learning_goals.split("\n").filter((l) => l.trim()).map((l, i) => (
                    <li key={i} className="flex gap-2"><Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "var(--sj-secondary)" }} /><span>{l.replace(/^[-*•]\s*/, "")}</span></li>
                  ))}
                </ul>
              </div>
            )}
            {courseFiles(detailFor).map((f, i) => (
              <button key={i} data-testid={`view-info-file-btn-${i}`} onClick={() => setViewer({ path: `/media/${f.file_id}`, fileName: f.name, contentType: f.content_type })} className="w-full flex items-center justify-between gap-2 rounded-xl border px-3 py-2.5 text-sm hover:bg-[#F1F8F5]" style={{ borderColor: "var(--sj-border)" }}>
                <span className="flex items-center gap-2 truncate" style={{ color: "var(--sj-text-2)" }}>
                  {courseFiles(detailFor).length > 1 && <span className="flex items-center justify-center h-5 w-5 shrink-0 rounded-full text-[11px] font-bold text-white" style={{ background: "var(--sj-primary)" }}>{i + 1}</span>}
                  <FileText className="h-4 w-4 shrink-0" style={{ color: "var(--sj-primary)" }} /> {f.name}
                </span>
                <span className="inline-flex items-center gap-1 shrink-0 font-semibold" style={{ color: "var(--sj-primary)" }}><Eye className="h-4 w-4" /> View</span>
              </button>
            ))}
          </div>
          {detailFor && !["pending", "closed"].includes(detailFor.registration_status) && (
            <DialogFooter>
              <button data-testid="detail-register-btn" disabled={registering === detailFor.id} onClick={() => { const c = detailFor; setDetailFor(null); register(c); }} className="rounded-full px-6 py-2.5 text-white font-semibold disabled:opacity-60" style={{ background: "var(--sj-primary)" }}>{detailFor.registration_status === "rejected" ? "Register again" : "Register for this course"}</button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      <DocViewer open={!!viewer} onClose={() => setViewer(null)} path={viewer?.path} fileName={viewer?.fileName} contentType={viewer?.contentType} />
    </div>
  );
}
